import { create } from "zustand"

import signImg from "@/assets/imgs/letter/sign.png"
import Toast from "@/components/Toast"
import { getWebcmsProgress } from "@/services/core"

export enum GlobalStage {
    LOADING,
    MAIN,
}

export interface CollectionItem {
    key: string
    ts: number
}

interface States {
    player: null | {
        state: {
            avatar?: string
        }
        collection: null | Record<string, CollectionItem[] | null>
    }
    globalStage: GlobalStage
    loadingDisplayProgress: number
    backgroundMapIdleMotionEnabled: boolean
    percent: number
    letter?: { to: string; contents: string[]; signature: string }
    gameStartTs: number
}
interface Actions {
    setPlayer: (payload: States["player"]) => void
    setGlobalStage: (payload: States["globalStage"]) => void
    setLoadingDisplayProgress: (progress: number) => void
    resetLoadingDisplayProgress: () => void
    setBackgroundMapIdleMotionEnabled: (enabled: boolean) => void
    resetBackgroundMapIdleMotionEnabled: () => void
    fetchMeta: () => Promise<void>
    setGameStartTs: (ts: number) => void
    updateCollectionGroup: (group: string, list: CollectionItem[]) => void
}

export type GlobalStore = States & Actions

export const useGlobalStore = create<GlobalStore>((set) => ({
    player: null,
    setPlayer: (payload) => {
        set({ player: payload })
    },
    globalStage: GlobalStage.LOADING,
    setGlobalStage: (payload) => {
        set({ globalStage: payload })
    },
    loadingDisplayProgress: 0,
    setLoadingDisplayProgress: (progress) => {
        set({ loadingDisplayProgress: Math.min(Math.max(progress, 0), 1) })
    },
    resetLoadingDisplayProgress: () => {
        set({ loadingDisplayProgress: 0 })
    },
    backgroundMapIdleMotionEnabled: true,
    setBackgroundMapIdleMotionEnabled: (enabled) => {
        set({ backgroundMapIdleMotionEnabled: enabled })
    },
    resetBackgroundMapIdleMotionEnabled: () => {
        set({ backgroundMapIdleMotionEnabled: true })
    },
    percent: 0,
    letter: {
        to: "Doctor,",
        contents: [
            "By the time you find this letter, you have likely already spent a great deal of time sifting through Mon3tr's memory banks. Perhaps the trials currently facing Rhodes Island have forced your hand, but even so, I would advise you to take moderate physical exercise and get sufficient sleep—it will be of far greater help to you than searching for answers within past memories.\nI chose to leave this letter for you, hoping it might bring some solace to you in that moment.\n\nYou no longer remember this;\nbut the universe once nurtured a radiant civilization—the limits of their knowledge practically boundless like the universe itself. Cognition reaches wherever imagination goes. Even the furthest star seemed no more distant than a book on a table.\nBut even so, they still could not answer a most fundamental question: \nIs there an innate, inherent meaning to life itself?\nPerhaps this is a final curtain left behind by our Creator, \na quest for meaning without any exits, life bound for loneliness at the end of our individual journeys.\n\nSages and scholars eventually gave up on finding answers from the observable universe, choosing instead to believe in our own instincts.\nBecause we can only believe that beyond incurable loneliness and inevitable confusion, that love likewise exists for certain.\nEven the depths of despair cannot stop people from locating echoes of our own existence among the sea of stars,\nshedding tears over traces of life separated by tens of thousands of light-years, before silence ultimately descends.\nI believe I have finally learned from my journey the feeling of this sentiment.\nLife is beautiful, and on a cosmic scale, 'life' and 'hope' are one and the same.",

            "I cannot restore your memories, and can only attempt to share this sentiment with you.\nI trust in your true nature, and I hope you can trust in your own choice and what it meant.\nYou will feel helpless and lost, because no one can accompany you forever, no one can comprehend your loneliness.\nRhodes Island has become a home for many,\nso I hope it can also become a refuge for you.\nI only wish to keep your own wishes safe, \nand I hope that at this journey's end you will be not be alone, and your companions will watch as you look forward to the future.",
        ],
        signature: signImg,
    },
    fetchMeta: async () => {
        const res = await getWebcmsProgress()
        if (res.code === 0) {
            const percent = res.data.results.rows[0]?.percent ?? 0
            set({ percent })
        }
    },
    gameStartTs: 0,
    setGameStartTs: (ts) => {
        set({ gameStartTs: ts })
    },
    updateCollectionGroup: (group, list) => {
        set((state) => {
            if (!state.player) return {}
            return {
                player: {
                    ...state.player,
                    collection: {
                        ...state.player.collection,
                        [group]: list,
                    },
                },
            }
        })
    },
}))

export const selectPlayer = (state: GlobalStore) => state.player
export const selectSetPlayer = (state: GlobalStore) => state.setPlayer
export const selectGlobalStage = (state: GlobalStore) => state.globalStage
export const selectSetGlobalStage = (state: GlobalStore) => state.setGlobalStage

export const usePlayer = () => useGlobalStore(selectPlayer)
export const useSetPlayer = () => useGlobalStore(selectSetPlayer)
export const selectPercent = (state: GlobalStore) => state.percent
export const selectFetchMeta = (state: GlobalStore) => state.fetchMeta
export const selectLetter = (state: GlobalStore) => state.letter

export const useGlobalStage = () => useGlobalStore(selectGlobalStage)
export const useSetGlobalStage = () => useGlobalStore(selectSetGlobalStage)
export const usePercent = () => useGlobalStore(selectPercent)
export const useFetchMeta = () => useGlobalStore(selectFetchMeta)
export const useLetter = () => useGlobalStore(selectLetter)

export const requireLogin = (): boolean => {
    const { player, setGlobalStage } = useGlobalStore.getState()
    if (player) return true
    setGlobalStage(GlobalStage.LOADING)
    Toast.message("Please log in first")
    return false
}
